import { useState, useCallback } from "react"
import { useCurrency } from "@/context/currency/currency-context"
import { useAuth } from "@/context/auth/auth-context"
import type { CurrencyCode } from "@/lib/currency-data"
import hive from "@/lib/api/hive"
import { Analytics } from "@/lib/analytics"

interface UseSaveCurrencyResult {
	saveCurrency: (code: CurrencyCode) => Promise<void>
	isSaving: boolean
	error: string | null
}

export function useSaveCurrency(): UseSaveCurrencyResult {
	const { currency, setCurrency } = useCurrency()
	const { token, isAuthenticated } = useAuth()
	const [isSaving, setIsSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const saveCurrency = useCallback(
		async (code: CurrencyCode) => {
			const previous = currency.code
			setError(null)
			setCurrency(code)
			Analytics.capture("Currency Changed", { from: previous, to: code })

			// Patch the user cache immediately so CurrencyInitializer doesn't
			// read the stale display_currency on the next page load and override back.
			const cachedUser = localStorage.getItem("beediv-user")
			if (cachedUser) {
				try {
					const parsed = JSON.parse(cachedUser) as Record<string, unknown>
					localStorage.setItem("beediv-user", JSON.stringify({ ...parsed, display_currency: code }))
				} catch {}
			}

			if (!isAuthenticated || !token) return

			setIsSaving(true)
			try {
				await hive.updateMe({ display_currency: code }, token)
			} catch {
				setCurrency(previous)
				// Revert the cache patch on failure
				if (cachedUser) {
					try {
						const parsed = JSON.parse(cachedUser) as Record<string, unknown>
						localStorage.setItem("beediv-user", JSON.stringify({ ...parsed, display_currency: previous }))
					} catch {}
				}
				setError("Failed to save currency preference. Please try again.")
			} finally {
				setIsSaving(false)
			}
		},
		[currency.code, isAuthenticated, token, setCurrency],
	)

	return { saveCurrency, isSaving, error }
}
